import { useMemo, useState } from "react";
import {
  calculateScenarios,
  parsePolishNumber,
  validateInput,
} from "./calculations";
import {
  Baseline,
  ChartBox,
  CTA,
  Details,
  Disclaimer,
  Error,
  Eyebrow,
  Field,
  FieldGrid,
  Form,
  Hero,
  Hint,
  Input,
  Layout,
  Lead,
  Legend,
  LegendItem,
  Page,
  Panel,
  ResultCard,
  ResultGrid,
  ResultHeading,
  ScheduleButton,
  Select,
  Submit,
  Table,
  TableControls,
  TableWrap,
  Title,
  AuditBox,
} from "./styled";
import { pushDataLayer } from "../../analytics";

const money = new Intl.NumberFormat("pl-PL", {
  style: "currency",
  currency: "PLN",
  maximumFractionDigits: 0,
});

const preciseMoney = new Intl.NumberFormat("pl-PL", {
  style: "currency",
  currency: "PLN",
  minimumFractionDigits: 2,
});

const formatPeriod = (months) => {
  if (months === 0) return "0 mies.";
  const years = Math.floor(months / 12);
  const rest = months % 12;
  return [years ? `${years} l.` : "", rest ? `${rest} mies.` : ""].filter(Boolean).join(" ");
};

const chartPoints = (schedule, maxMonths, maxBalance) => {
  const values = [{ month: 0, balance: maxBalance }, ...schedule];
  const sampled = values.filter((_, index) => index === 0 || index === values.length - 1 || index % Math.max(1, Math.ceil(values.length / 60)) === 0);
  return sampled
    .map(({ month, balance }) => {
      const x = 16 + (month / Math.max(1, maxMonths)) * 688;
      const y = 220 - (balance / Math.max(1, maxBalance)) * 190;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
};

const BalanceChart = ({ result, principal }) => {
  const maxMonths = result.baseline.months;
  return (
    <ChartBox>
      <Legend>
        <LegendItem $color="#8b938c">Bez nadpłat</LegendItem>
        <LegendItem $color="#ffa900">Skrócenie okresu</LegendItem>
        <LegendItem $color="#1c5d27">Obniżanie raty</LegendItem>
      </Legend>
      <svg viewBox="0 0 720 250" role="img" aria-labelledby="chart-title chart-desc">
        <title id="chart-title">Porównanie salda kredytu</title>
        <desc id="chart-desc">Wykres pokazuje zmianę salda bez nadpłat oraz w dwóch wariantach nadpłacania.</desc>
        {[30, 77.5, 125, 172.5, 220].map((y) => (
          <line key={y} x1="16" x2="704" y1={y} y2={y} stroke="#e6e9e5" strokeWidth="1" />
        ))}
        <polyline points={chartPoints(result.baseline.schedule, maxMonths, principal)} fill="none" stroke="#8b938c" strokeWidth="4" strokeLinecap="round" />
        <polyline points={chartPoints(result.shorten.schedule, maxMonths, principal)} fill="none" stroke="#ffa900" strokeWidth="5" strokeLinecap="round" />
        <polyline points={chartPoints(result.lowerPayment.schedule, maxMonths, principal)} fill="none" stroke="#1c5d27" strokeWidth="5" strokeLinecap="round" />
        <text x="16" y="244" fontSize="11" fill="#6c726c">start</text>
        <text x="704" y="244" textAnchor="end" fontSize="11" fill="#6c726c">{formatPeriod(maxMonths)}</text>
      </svg>
    </ChartBox>
  );
};

const initialValues = {
  principal: "350 000",
  months: "240",
  annualRate: "6,50",
  monthlyOverpayment: "500",
  monthlyOverpaymentStart: "1",
  monthlyOverpaymentMonths: "0",
  oneTimeOverpayment: "0",
  oneTimeOverpaymentMonth: "1",
  rateType: "fixed",
  paymentType: "equal",
};

const KalkulatorNadplat = () => {
  const [values, setValues] = useState(initialValues);
  const [submittedValues, setSubmittedValues] = useState(initialValues);
  const [hasCalculated, setHasCalculated] = useState(false);
  const [errors, setErrors] = useState({});
  const [input, setInput] = useState({
    principal: 350000,
    months: 240,
    annualRate: 6.5,
    monthlyOverpayment: 500,
    monthlyOverpaymentStart: 1,
    monthlyOverpaymentMonths: 0,
    oneTimeOverpayment: 0,
    oneTimeOverpaymentMonth: 1,
    paymentType: "equal",
  });
  const [tableStrategy, setTableStrategy] = useState("lowerPayment");
  const [scheduleOpen, setScheduleOpen] = useState(false);

  const result = useMemo(() => calculateScenarios(input), [input]);
  const isDirty = Object.keys(values).some((key) => values[key] !== submittedValues[key]);
  const firstReducedPayment = result.lowerPayment.firstPaymentAfterOverpayment;
  const finalReducedPayment = result.lowerPayment.paymentAfterLastOverpayment;
  const hasAppliedOverpayment = result.lowerPayment.schedule.some((row) => row.overpayment > 0);

  const update = (event) => setValues((current) => ({ ...current, [event.target.name]: event.target.value }));

  const handleSubmit = (event) => {
    event.preventDefault();
    const parsed = {
      principal: parsePolishNumber(values.principal),
      months: parsePolishNumber(values.months),
      annualRate: parsePolishNumber(values.annualRate),
      monthlyOverpayment: parsePolishNumber(values.monthlyOverpayment || "0"),
      monthlyOverpaymentStart: parsePolishNumber(values.monthlyOverpaymentStart || "1"),
      monthlyOverpaymentMonths: parsePolishNumber(values.monthlyOverpaymentMonths || "0"),
      oneTimeOverpayment: parsePolishNumber(values.oneTimeOverpayment || "0"),
      oneTimeOverpaymentMonth: parsePolishNumber(values.oneTimeOverpaymentMonth || "1"),
      paymentType: values.paymentType,
    };
    const nextErrors = validateInput(parsed);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length === 0) {
      setInput(parsed);
      setSubmittedValues({ ...values });
      setHasCalculated(true);
      pushDataLayer("sf_overpayment_calculator_submit", {
        payment_type: parsed.paymentType,
        rate_type: values.rateType,
      });
      window.requestAnimationFrame(() => document.getElementById("wyniki-kalkulatora")?.scrollIntoView({ behavior: "smooth", block: "start" }));
    }
  };

  const schedule = result[tableStrategy].schedule;
  const strategyNames = {shorten: "Skracanie okresu", lowerPayment: "Obniżanie raty", baseline: "Bez nadpłat"};
  const showSchedule = (strategy) => {
    setTableStrategy(strategy);
    setScheduleOpen(true);
    window.requestAnimationFrame(() => document.getElementById("harmonogram-kalkulatora")?.scrollIntoView({behavior: "smooth", block: "start"}));
  };

  const goToForm = () => window.setTimeout(() => document.getElementById("formularz")?.scrollIntoView({ behavior: "smooth" }), 80);

  return (
    <Page>
      <Hero>
        <Eyebrow>Bezpłatne narzędzie</Eyebrow>
        <Title>Sprawdź, ile możesz zyskać dzięki nadpłacie kredytu</Title>
        <Lead>Porównaj skrócenie okresu z obniżaniem kolejnych rat. Wynik otrzymasz od razu, bez podawania telefonu i adresu e-mail.</Lead>
      </Hero>

      <Layout>
        <Panel>
          <Form onSubmit={handleSubmit} noValidate>
            <Field>
              Rodzaj rat
              <Select name="paymentType" value={values.paymentType} onChange={update} aria-invalid={Boolean(errors.paymentType)}>
                <option value="equal">Raty równe</option>
                <option value="decreasing">Raty malejące</option>
              </Select>
              {errors.paymentType && <Error role="alert">{errors.paymentType}</Error>}
            </Field>
            <Field>
              Saldo kredytu pozostałe do spłaty
              <Hint>To nie jest pierwotna kwota kredytu ani suma przyszłych rat.</Hint>
              <Input name="principal" value={values.principal} onChange={update} inputMode="decimal" $invalid={errors.principal} aria-invalid={Boolean(errors.principal)} />
              {errors.principal && <Error role="alert">{errors.principal}</Error>}
            </Field>
            <Field>
              Liczba miesięcy pozostałych do spłaty
              <Input name="months" value={values.months} onChange={update} inputMode="numeric" $invalid={errors.months} aria-invalid={Boolean(errors.months)} />
              {errors.months && <Error role="alert">{errors.months}</Error>}
            </Field>
            <Field>
              Aktualne oprocentowanie nominalne w skali roku
              <Hint>Znajdziesz je w aplikacji banku lub harmonogramie. Nie wpisuj RRSO.</Hint>
              <Input name="annualRate" value={values.annualRate} onChange={update} inputMode="decimal" $invalid={errors.annualRate} aria-invalid={Boolean(errors.annualRate)} />
              {errors.annualRate && <Error role="alert">{errors.annualRate}</Error>}
            </Field>
            <Field>
              Typ oprocentowania
              <Select name="rateType" value={values.rateType} onChange={update}>
                <option value="fixed">Okresowo stałe</option>
                <option value="variable">Zmienne</option>
                <option value="unknown">Nie wiem</option>
              </Select>
              {values.rateType !== "fixed" && <Hint>Symulacja przyjmuje wpisane oprocentowanie przez cały okres. Przy oprocentowaniu zmiennym rzeczywiste raty będą się zmieniać.</Hint>}
              {values.rateType === "fixed" && <Hint>Symulacja zachowuje wpisane oprocentowanie także po zakończeniu okresu stałej stopy. Rzeczywiste oprocentowanie może się wtedy zmienić.</Hint>}
            </Field>
            <Field>
              Miesięczna nadpłata
              <Input name="monthlyOverpayment" value={values.monthlyOverpayment} onChange={update} inputMode="decimal" $invalid={errors.monthlyOverpayment} aria-invalid={Boolean(errors.monthlyOverpayment)} />
              {errors.monthlyOverpayment && <Error role="alert">{errors.monthlyOverpayment}</Error>}
            </Field>
            <FieldGrid>
              <Field>
                Od którego miesiąca?
                <Input name="monthlyOverpaymentStart" value={values.monthlyOverpaymentStart} onChange={update} inputMode="numeric" $invalid={errors.monthlyOverpaymentStart} aria-invalid={Boolean(errors.monthlyOverpaymentStart)} />
                {errors.monthlyOverpaymentStart && <Error role="alert">{errors.monthlyOverpaymentStart}</Error>}
              </Field>
              <Field>
                Przez ile miesięcy?
                <Hint>Wpisz 0, jeśli chcesz nadpłacać do końca.</Hint>
                <Input name="monthlyOverpaymentMonths" value={values.monthlyOverpaymentMonths} onChange={update} inputMode="numeric" $invalid={errors.monthlyOverpaymentMonths} aria-invalid={Boolean(errors.monthlyOverpaymentMonths)} />
                {errors.monthlyOverpaymentMonths && <Error role="alert">{errors.monthlyOverpaymentMonths}</Error>}
              </Field>
            </FieldGrid>
            <FieldGrid>
              <Field>
                Dodatkowa jednorazowa nadpłata
                <Input name="oneTimeOverpayment" value={values.oneTimeOverpayment} onChange={update} inputMode="decimal" $invalid={errors.oneTimeOverpayment} aria-invalid={Boolean(errors.oneTimeOverpayment)} />
                {errors.oneTimeOverpayment && <Error role="alert">{errors.oneTimeOverpayment}</Error>}
              </Field>
              <Field>
                W którym miesiącu?
                <Hint>Miesiąc 1 oznacza początek symulacji.</Hint>
                <Input name="oneTimeOverpaymentMonth" value={values.oneTimeOverpaymentMonth} onChange={update} inputMode="numeric" $invalid={errors.oneTimeOverpaymentMonth} aria-invalid={Boolean(errors.oneTimeOverpaymentMonth)} />
                {errors.oneTimeOverpaymentMonth && <Error role="alert">{errors.oneTimeOverpaymentMonth}</Error>}
              </Field>
            </FieldGrid>
            <Hint>Marża banku nie jest potrzebna — wpisz łączne oprocentowanie nominalne. Miesięczna nadpłata obniża następną ratę w wariancie obniżania rat.</Hint>
            <Submit type="submit">Oblicz korzyść z nadpłat</Submit>
          </Form>
        </Panel>

        <Panel id="wyniki-kalkulatora" aria-live="polite">
          <ResultHeading>
            <div><h2>{hasCalculated ? "Twój wynik" : "Przykładowy wynik"}</h2><p>{input.paymentType === "decreasing" ? "Raty malejące" : "Raty równe"} — porównanie dwóch sposobów rozliczania nadpłat</p></div>
          </ResultHeading>
          {isDirty && <p role="status"><strong>Dane zostały zmienione. Poniżej widzisz wynik dla poprzednich danych — kliknij „Oblicz korzyść z nadpłat”, aby go zaktualizować.</strong></p>}
          <Baseline>
            <span>{input.paymentType === "decreasing" ? "Pierwsza rata bez nadpłat" : "Modelowa rata bez nadpłat"}: <strong>{preciseMoney.format(result.baseline.originalPayment)}</strong></span>
            <span>Odsetki bez nadpłat: <strong>{money.format(result.baseline.totalInterest)}</strong></span>
          </Baseline>
          <ResultGrid>
            <ResultCard $accent>
              <h3>Skracanie okresu</h3>
              <dl>
                <div><dt>Oszczędność odsetek</dt><dd>{money.format(result.shortenSavings)}</dd></div>
                <div><dt>Nowy czas spłaty</dt><dd>{formatPeriod(result.shorten.months)}</dd></div>
                <div><dt>Kredyt krótszy o</dt><dd>{formatPeriod(result.shortenMonthsSaved)}</dd></div>
                <div><dt>Łączne odsetki</dt><dd>{money.format(result.shorten.totalInterest)}</dd></div>
              </dl>
              <ScheduleButton type="button" onClick={() => showSchedule("shorten")}>Harmonogram skracania okresu</ScheduleButton>
            </ResultCard>
            <ResultCard>
              <h3>Obniżanie kolejnych rat</h3>
              <dl>
                <div><dt>Oszczędność odsetek</dt><dd>{money.format(result.lowerPaymentSavings)}</dd></div>
                <div>
                  <dt>{firstReducedPayment ? `Rata po pierwszej nadpłacie (mies. ${firstReducedPayment.month})` : "Rata po nadpłacie"}</dt>
                  <dd>{firstReducedPayment ? preciseMoney.format(firstReducedPayment.amount) : hasAppliedOverpayment ? "Kredyt spłacony" : "Brak nadpłat"}</dd>
                </div>
                {hasAppliedOverpayment && <div>
                  <dt>{finalReducedPayment ? `Rata po zakończeniu nadpłat (mies. ${finalReducedPayment.month})` : "Po zakończeniu nadpłat"}</dt>
                  <dd>{finalReducedPayment ? preciseMoney.format(finalReducedPayment.amount) : "Kredyt spłacony"}</dd>
                </div>}
                {finalReducedPayment && <div>
                  <dt>Mniej niż bez nadpłat w tym samym miesiącu</dt>
                  <dd>{preciseMoney.format(Math.max(0, result.baseline.schedule[finalReducedPayment.month - 1].scheduledPayment - finalReducedPayment.amount))}</dd>
                </div>}
                <div><dt>Czas spłaty</dt><dd>{formatPeriod(result.lowerPayment.months)}</dd></div>
                <div><dt>Łączne odsetki</dt><dd>{money.format(result.lowerPayment.totalInterest)}</dd></div>
              </dl>
              <p>Kwoty dotyczą samej raty, bez dodatkowej nadpłaty. {input.paymentType === "decreasing" ? "Raty maleją również bez nadpłat; porównujemy ten sam miesiąc." : "Po każdej miesięcznej nadpłacie następna rata jest przeliczana. Po zakończeniu nadpłat pozostaje na obniżonym poziomie przy niezmiennym oprocentowaniu."}</p>
              <ScheduleButton type="button" onClick={() => showSchedule("lowerPayment")}>Harmonogram obniżania raty</ScheduleButton>
            </ResultCard>
          </ResultGrid>
          <BalanceChart result={result} principal={input.principal} />
          <Details id="harmonogram-kalkulatora" open={scheduleOpen} onToggle={(event) => setScheduleOpen(event.currentTarget.open)}>
            <summary>Zobacz szczegółowy harmonogram</summary>
            <TableControls>
              <span>Wariant:</span>
              <Select value={tableStrategy} onChange={(event) => setTableStrategy(event.target.value)} aria-label="Wariant harmonogramu">
                <option value="shorten">Skracanie okresu</option>
                <option value="lowerPayment">Obniżanie raty</option>
                <option value="baseline">Bez nadpłat</option>
              </Select>
            </TableControls>
            <p><strong>{strategyNames[tableStrategy]} · {input.paymentType === "decreasing" ? "raty malejące" : "raty równe"}</strong></p>
            <p>„Rata” nie zawiera nadpłaty. „Łączna wpłata” to rata i nadpłata razem. Kapitał obejmuje również nadpłatę. Na telefonie przesuń tabelę w bok.</p>
            <TableWrap>
              <Table>
                <caption>{strategyNames[tableStrategy]} — harmonogram miesięczny</caption>
                <thead><tr><th>Miesiąc</th><th>Rata</th><th>Nadpłata</th><th>Łączna wpłata</th><th>Odsetki</th><th>Kapitał</th><th>Saldo</th></tr></thead>
                <tbody>{schedule.map((row) => <tr key={row.month}><td>{row.month}</td><td>{preciseMoney.format(row.scheduledPayment)}</td><td>{preciseMoney.format(row.overpayment)}</td><td>{preciseMoney.format(row.totalPayment)}</td><td>{preciseMoney.format(row.interest)}</td><td>{preciseMoney.format(row.principal)}</td><td>{preciseMoney.format(row.balance)}</td></tr>)}</tbody>
              </Table>
            </TableWrap>
          </Details>
          <CTA to="/?source=kalkulator-nadplat" data-contact-cta onClick={goToForm}>Omów wynik z nami</CTA>
        </Panel>
      </Layout>

      <AuditBox>
        <div>
          <Eyebrow>Drugi krok</Eyebrow>
          <h2>Czy Twój obecny kredyt nadal ma dobre warunki?</h2>
          <p>Nadpłata może skrócić spłatę, ale czasem większą różnicę daje przeniesienie kredytu. Podczas bezpłatnego audytu sprawdzimy oprocentowanie, ratę, pozostały okres i możliwe koszty zmiany banku.</p>
          <p><strong>Audyt i nasza obsługa nie oznaczają dodatkowej opłaty po Twojej stronie.</strong> Jeśli po analizie zdecydujesz się na kredyt, wynagrodzenie za pośrednictwo wypłaca bank. Nie doliczamy własnego narzutu do oprocentowania, prowizji bankowej ani raty.</p>
          <div className="guide-note">
            <strong>Przed nadpłatą sprawdź umowę i bankowość internetową</strong>
            <span>Prowizję za wcześniejszą spłatę, sposób składania dyspozycji oraz to, czy bank obniży ratę, czy skróci okres.</span>
          </div>
        </div>
        <CTA to="/?source=kalkulator-nadplat" data-contact-cta onClick={goToForm}>Poproś o audyt kredytu</CTA>
      </AuditBox>

      <Disclaimer>
        <h2>Jak czytać ten wynik?</h2>
        <p>To orientacyjna symulacja wybranego rodzaju rat. Jednorazowa nadpłata jest liczona na początku wskazanego miesiąca, a nadpłata miesięczna po racie. Kalkulator zakłada niezmienne oprocentowanie wpisane powyżej. Bank może naliczać odsetki dziennie i stosować inne zaokrąglenia. Wynik nie uwzględnia prowizji za nadpłatę, aneksów, ubezpieczeń ani innych kosztów i nie jest ofertą banku ani gwarantowanym harmonogramem.</p>
        <p>Dla rat malejących model dzieli saldo początkowe przez pozostałą liczbę rat i dodaje odsetki od aktualnego salda. Przy skracaniu okresu zachowuje tę część kapitałową; przy obniżaniu rat przelicza ją z pozostałego salda i liczby miesięcy. Dlatego nawet przy skracaniu okresu rata malejąca może spadać. Sposób rozliczenia nadpłaty w konkretnym banku może być inny.</p>
        <p>Warianty mają takie same planowane nadpłaty, ale różny łączny wydatek miesięczny: rata plus nadpłata. Nie jest to porównanie przy identycznym budżecie. Po wcześniejszej spłacie kolejne zaplanowane nadpłaty nie są wykonywane.</p>
      </Disclaimer>
    </Page>
  );
};

export default KalkulatorNadplat;

